/**
 * protocol.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import { deserialize, ObjectId, serialize } from "bson";
import { DateTime } from "luxon";
import moment from "moment-timezone";

import { DeviceNode } from "../models/auth";
import { RatchetTree, UpdatePathNode } from "../models/crypto";
import { ErrorCode, GroupError, RecoveryTooOldError } from "../models/error";
import {
  BsonGroup, BsonRatchetTreeNode, Group, GroupSecrets,
  Ratchet,
  RatchetTreeNode, Keys, GroupPKI,
  ProtocolScheme
} from "../models/group";
import {
  ContentMessage, MessageData, MessageFrame, MessageFrameType,
  MessageType, SenderData, RemoveMsgMessage, NewDataKeyMessage, DeviceJoinedMessage,
  DevicesAddedMessage,
  HistoryPreservationMessage,
  NotifyUser
} from "../models/message";

import { cryptoServiceFactory, CryptoService } from "../services/crypto.service";
import { treeServiceFactory, TreeService, MAX_RESUMPTION_COUNT } from "../services/tree.service";
import { bufferToBase64, hexToBuffer } from "./encoding.service";
import { reportErrorInfo } from "./error.service";

export interface AddDevicesMessage extends MessageData {
  updatePath: Array<UpdatePathNode>,
  devices: Array<DeviceNode>,
  parents: Array<RatchetTreeNode>,
  init_secret?: Uint8Array,
  treeHash: string,
  derivationHash?: string
}

export interface RecoverMessage extends MessageData {
  updatePath: Array<UpdatePathNode>,
  resumeFromEpoch: number,
  init_secret?: Uint8Array,
  treeHash: string,
  derivationHash: string
}

export interface RemoveMemberMessage extends MessageData {
  removed: number,
  updatePath: Array<UpdatePathNode>,
  treeHash: string,
  derivationHash?: string
}
export interface ExternalWelcomeMessage extends MessageData {
  resumption_secrets: Array<{ epoch: number, secret: Uint8Array }>,
  data_secrets: Array<{ _id: string, secret: Uint8Array }>,
  private_keys: Array<{ i: number, priv: string }>,
  group_keys?: Array<Uint8Array>
}

export interface SchemeChangeMessage {
  protocolScheme: ProtocolScheme,
  group_key?: Uint8Array | undefined,
  updatePath: Array<UpdatePathNode>,
  init_secret?: Uint8Array,
  treeHash: string,
  derivationHash?: string
}

export interface ProtocolVersioning {
  pki: GroupPKI
}
interface Verifier {
  publicKey?: string,
  verifyKey?: string
}

interface ProcessedMessage {
  buf: Uint8Array,
  senderNode: RatchetTreeNode,
  senderIndex: number
}

interface ProcessedSharedSecretMessage {
  buf: Uint8Array,
  senderUserID: string,
  senderDeviceID: string
}

export interface ProtocolServiceDelegate {
  selfDeviceID: string,
  selfUserID: string,
  selfKeys: Keys,
  getDevice(deviceID: string): Promise<DeviceNode | null | undefined>,
  getRatchetTree(groupID: string): Promise<RatchetTree | null | undefined>,
  createRatchetTree(rtree: RatchetTree): Promise<void>,
  updateRatchetTree(rtree: RatchetTree): Promise<void>,
  removeRatchetTree(rtree: RatchetTree): Promise<void>,
  removeContent(groupID: string, accountID: string): Promise<void>,
  prackMessage(accountID: string, groupID: string, messageID: string): Promise<boolean>,
  processServerRequest(frame: MessageFrame): Promise<void>,
  didProcessWelcome(frame: MessageFrame, rtree: RatchetTree): Promise<void>,
  onApplicationMessage(message: MessageData, frame: MessageFrame, rtree: RatchetTree): Promise<void>,
  onAppHistoricalMessage(message: MessageData, frame: MessageFrame, rtree: RatchetTree): Promise<void>,
  sendFrame(accountID: string, groupID: string, frame: MessageFrame): Promise<ArrayBuffer | void>,
  sendFrameTo(accountID: string, groupID: string, deviceID: string, frame: MessageFrame): Promise<ArrayBuffer | void>
}

export interface ProtocolService {
  delegate: ProtocolServiceDelegate | undefined,
  init(): Promise<void>,
  createRatchetTree(group: Group, devices: Array<DeviceNode>, delegate?: ProtocolServiceDelegate): Promise<RatchetTree>,
  rewelcome(rtree: RatchetTree, oldTree: RatchetTree | null,
    devices: Array<DeviceNode>, protoVersion: number, delegate?: ProtocolServiceDelegate): Promise<RatchetTree>,
  join(group: Group, device: DeviceNode, delegate?: ProtocolServiceDelegate): Promise<RatchetTree>,
  recover(rtree: RatchetTree, oldTree: RatchetTree, delegate?: ProtocolServiceDelegate): Promise<RatchetTree>,
  addMembers(rtreeOrGroupID: RatchetTree | string, devices: Array<DeviceNode>, delegate?: ProtocolServiceDelegate): Promise<RatchetTree>,
  removeMember(rtreeOrGroupID: RatchetTree | string, deviceID: string, delegate?: ProtocolServiceDelegate): Promise<[RatchetTree, boolean]>
  sendMessage(rtreeOrGroupID: RatchetTree | string, message: MessageData,
    userIDsToNotify?: Array<NotifyUser>,
    frameType?: MessageFrameType,
    delegate?: ProtocolServiceDelegate): Promise<[RatchetTree, MessageFrame]>,
  setProtocolScheme(rtree: RatchetTree, protocolScheme: ProtocolScheme, delegate?: ProtocolServiceDelegate): Promise<RatchetTree>,

  processMessageFrame(frame: MessageFrame, delegate?: ProtocolServiceDelegate): Promise<any>,
  processStoredMessageFrame(frame: MessageFrame, delegate?: ProtocolServiceDelegate): Promise<any>
}

class ProtocolServiceImpl implements ProtocolService {
  public delegate: ProtocolServiceDelegate | undefined;
  private cryptoService: CryptoService;
  private treeService: TreeService;
  constructor(cryptoService?: CryptoService) {
    this.delegate = undefined;
    this.cryptoService = cryptoService || cryptoServiceFactory();
    this.treeService = treeServiceFactory();
  }

  init(): Promise<void> {
    return this.cryptoService.init();
  }

  serializeGroup(rtree: RatchetTree, myDevice: string): Uint8Array {
    const myIndex = rtree.indexOfDevice(myDevice);
    if (myIndex < 0) {
      console.error("Couldn't find index of", myDevice, "in group:", rtree);
      throw Error("Couldn't find index of this device in group");
    }
    const myNode = rtree.treenodes[myIndex];
    const aPrivKey = myNode.privateKey;
    const aSignKey = myNode.signKey;
    myNode.privateKey = undefined;
    myNode.signKey = undefined;
    delete myNode.privateKey;
    const unencrypted = serialize(bsonifyGroup(rtree));
    myNode.privateKey = aPrivKey;
    myNode.signKey = aSignKey;
    return unencrypted;
  }

  packMessagePayload(data: any): Uint8Array {
    return serialize(data);
  }
  unpackMessagePayload(buf: Uint8Array): MessageData {
    return deserialize(buf, { promoteBuffers: true, promoteValues: true }) as MessageData;
  }
  packFrame(frame: MessageFrame): Uint8Array {
    return serialize(frame);
  }
  unpackFrame(buf: Uint8Array): MessageFrame {
    return deserialize(buf, { promoteBuffers: true, promoteValues: true }) as MessageFrame;
  }

  decryptAndUnpack(buf: Uint8Array, privateKey: string | Keys, rtree: ProtocolVersioning): any {
    let decrypted: Uint8Array;
    if (rtree.pki === GroupPKI.crystals) {
      const decryptKey = this.cryptoService.importKyberPriKey(
        typeof privateKey === "string" ? privateKey : privateKey.kyberPrivateKey);
      decrypted = this.cryptoService.kyberDecrypt(buf, decryptKey);
      decryptKey.delete();
    } else {
      const decryptKey = this.cryptoService.importPrivateKey(
        typeof privateKey === "string" ? privateKey : privateKey.privateKey);
      decrypted = this.cryptoService.decapsulate(buf, decryptKey);
    }
    return deserialize(decrypted, { promoteBuffers: true, promoteValues: true });
  }

  makeTreeNode(userID: string, deviceID: string, version: ProtocolVersioning, keys: Keys): RatchetTreeNode {
    const node: RatchetTreeNode = {
      userID: userID,
      deviceID: deviceID,
      generation: 0,
      handshakeGeneration: 0
    };
    if (version.pki === GroupPKI.crystals) {
      node.privateKey = keys.kyberPrivateKey;
      node.publicKey = keys.kyberPrivateKey;
      node.verifyKey = keys.diPublicKey;
      node.signKey = keys.diPrivateKey;
    } else {
      node.privateKey = keys.privateKey;
      node.publicKey = keys.publicKey;
    }
    return node;
  }

  verifyFrameSignature(frame: MessageFrame, verifyingEntity: Verifier, rtree: ProtocolVersioning): void {
    if (!frame.signature) {
      throw new Error("Signature missing on Frame");
    }
    const signature = frame.signature;
    const sigData = this.treeService.makeSignatureData(frame);
    let success: boolean;
    if (rtree.pki === GroupPKI.crystals) {
      if (!verifyingEntity.verifyKey) {
        throw new Error("Missing verifyKey for signature verification");
      }
      const key = this.cryptoService.importDilithiumPubKey(verifyingEntity.verifyKey);
      success = this.cryptoService.dilithiumVerify(signature, sigData, key);
      key.delete();
    } else {
      if (!verifyingEntity.publicKey) {
        throw new Error("Missing publicKey for signature verification");
      }
      const key = this.cryptoService.importPublicKey(verifyingEntity.publicKey);
      success = this.cryptoService.verifySignature(sigData, signature, key);
    }

    if (!success) {
      // console.log("signature verification failed");
      // console.log("  index:", senderIndex);
      // console.log("  sender pub key:", senderNode.publicKey);
      // console.log("  signature:", bufferToBase64(signature));
      // console.log("  sigData:", bufferToBase64(sigData));
      //printGroup(group);
      throw new Error("Signature Verification Failed for Message");
    }
  }

  async ratchetEpoch(rtree: RatchetTree, fromIndex: number,
    init_secret: Uint8Array, psk_secret?: Uint8Array): Promise<[Array<UpdatePathNode>, string, string]> {
    //init_secret = init_secret || await this.cryptoService.generateRandSecret();
    const [updatePath, commit_secret] = await this.treeService.doTreeUpdatePath(rtree, fromIndex);

    const encryption_secret = await this.treeService.keyScheduleDerivation(
      rtree, init_secret, commit_secret, rtree.epoch + 1, psk_secret);
    //console.log("building ratchets with encryption_secret:",
    //  bufferToBase64(encryption_secret));
    await this.treeService.computeSecretTree(rtree, rtree.rootIndex, undefined, encryption_secret);

    const [treeHash, devHash] = await bluebird.all([
      this.treeService.computeTreeNodeHash(rtree),
      this.treeService.computeKeyDerivationHash(init_secret, commit_secret, psk_secret)
    ]);

    rtree.epoch++;

    //console.log("starting epoch", rtree._id, rtree.epoch);
    return [updatePath, bufferToBase64(treeHash), bufferToBase64(devHash)];
  }

  async ratchetEpochFromUpdate(rtree: RatchetTree, updatePath: Array<UpdatePathNode>,
    myIndex: number, senderIndex: number, init_secret: Uint8Array, psk_secret?: Uint8Array): Promise<[string, string]> {
    const [updateNode, path_secret] = this.treeService.decryptUpdatePath(
      rtree, updatePath, myIndex, senderIndex);

    const commit_secret = await this.treeService.fillTreeSecrets(rtree,
      updatePath, updateNode.index, path_secret, updateNode.publicKey);

    const encryption_secret = await this.treeService.keyScheduleDerivation(
      rtree, init_secret, commit_secret, rtree.epoch + 1,
      psk_secret);

    // console.log("commit_secret:", bufferToBase64(commit_secret));
    // console.log("encryption_secret:", bufferToBase64(encryption_secret));

    await this.treeService.computeSecretTree(rtree, rtree.rootIndex, undefined, encryption_secret);

    rtree.epoch++;

    //console.log("starting epoch", rtree._id, rtree.epoch);
    const [t, d] = await bluebird.all([
      this.treeService.computeTreeNodeHash(rtree),
      this.treeService.computeKeyDerivationHash(init_secret, commit_secret,
        psk_secret)
    ]);
    return [bufferToBase64(t), bufferToBase64(d)];
  }

  async sendMessage(rtreeOrGroupID: RatchetTree | string, message: MessageData,
    userIDsToNotify?: Array<NotifyUser>,
    frameType: MessageFrameType = MessageFrameType.MESSAGE,
    delegate?: ProtocolServiceDelegate): Promise<[RatchetTree, MessageFrame]> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    const rtree = typeof rtreeOrGroupID === "string" ? await delegate.getRatchetTree(rtreeOrGroupID) : rtreeOrGroupID;
    if (!rtree) { throw new GroupError("Encryption Keys For Group Not Found", 400); }
    if (!rtree.accountID) { throw new Error("Account ID missing on group"); }

    const myIndex = rtree.indexOfDevice(delegate.selfDeviceID);
    if (myIndex < 0) {
      throw new Error("Device not member of group");
    }
    let ratchetProp: keyof Ratchet = "ratchet_secret";
    let contentType: "application" | "commit" = "application";
    if (frameType === MessageFrameType.NEW_DATA_KEY) {
      ratchetProp = "handshake_secret";
      contentType = "commit"
    }
    const payload = this.packMessagePayload(message);
    const frame = await (rtree.protocolScheme === ProtocolScheme.mlsSharedSecret ?
      this.frameForDataSharedSecret(rtree, delegate.selfUserID, delegate.selfDeviceID, delegate.selfKeys,
        frameType, payload, userIDsToNotify) :
      this.frameForData(rtree, delegate.selfUserID, delegate.selfDeviceID,
        frameType, payload, ratchetProp, contentType, userIDsToNotify));

    await delegate.sendFrame(rtree.accountID, rtree._id, frame);

    this.treeService.updateMsgCounter(rtree, frame.msgCounter);
    //printGroup(rtree);
    return [rtree, frame];
  }

  async welcomeMessage(rtree: RatchetTree, device: DeviceNode, accountID: string, myUserID: string,
    myDevice: string, keys: Keys, serializedGroup: Uint8Array,
    frameType: MessageFrameType.WELCOME | MessageFrameType.REWELCOME): Promise<MessageFrame> {
    let data: Uint8Array;
    const isPQE = rtree.pki === GroupPKI.crystals;
    if (isPQE) {
      if (!device.kyberKey) {
        throw new Error("No kyber key for device");
      }
      const devPublicKey = this.cryptoService.importKyberPubKey(device.kyberKey);
      data = await this.cryptoService.kyberEncrypt(serializedGroup, devPublicKey);
      devPublicKey.delete();
    } else {
      if (!device.publicKey) {
        throw new Error("No public key for device");
      }
      const devPublicKey = this.cryptoService.importPublicKey(device.publicKey);
      data = await this.cryptoService.encapsulate(serializedGroup, devPublicKey/*, privKey*/);
    }

    /*const senderData: SenderData = {
      senderID: myUserID,
      deviceID: myDevice,
      generation: 0,
      sender: 0,
      reuseGuard: new Uint8Array(0)
    };*/

    const frame: MessageFrame = {
      _id: new ObjectId().toHexString(),
      cmd: frameType,
      accountID: accountID,
      groupID: rtree._id,
      created: new Date(),
      fromUserID: myUserID,
      fromDeviceID: myDevice,
      epoch: rtree.epoch,
      msgCounter: rtree.msgCounter,
      data: data,
      senderData: new Uint8Array(0),
      pki: rtree.pki,
      version: rtree.protoVersion
    };

    const sigData = this.treeService.makeSignatureData(frame);

    if (isPQE) {
      //console.log("pub key for verifying this msg:", keys.diPublicKey.length, keys.diPublicKey);
      //console.log("sig data:", bufferToBase64(sigData));
      const signKey = this.cryptoService.importDilithiumPriKey(keys.diPrivateKey);
      frame.signature = this.cryptoService.dilithiumSign(sigData, signKey);
      signKey.delete();
      //console.log("signature:", (device as any).username, frame.signature.length, bufferToBase64(frame.signature));
    } else {
      //console.log("sig data:", bufferToBase64(sigData));
      //console.log("verifyWith:", keys.publicKey);
      const signKey = this.cryptoService.importPrivateKey(keys.privateKey);
      frame.signature = this.cryptoService.sign(sigData, signKey);
    }
    return frame;
  }

  async welcomeMessagesFor(rtree: RatchetTree, devices: DeviceNode[],
    myUserID: string, myDeviceID: string, delegate: ProtocolServiceDelegate): Promise<MessageFrame[]> {
    if (!rtree.accountID) {
      throw Error("Missing accountID on group");
    }
    const unencrypted = this.serializeGroup(rtree, myDeviceID);
    return bluebird.map(devices, dev => {
      return this.welcomeMessage(rtree, dev, rtree.accountID, myUserID, myDeviceID,
        delegate.selfKeys, unencrypted, MessageFrameType.WELCOME);
    });
  }

  async *genWelcomeMessagesFor(rtree: RatchetTree, accountID: string, devices: Array<DeviceNode>,
    myUserID: string, myDeviceID: string, delegate: ProtocolServiceDelegate): AsyncGenerator<[DeviceNode, MessageFrame], void, void> {
    const unencrypted = this.serializeGroup(rtree, myDeviceID);
    for (const dev of devices) {
      if (rtree.pki === GroupPKI.crystals && !dev.kyberKey) {
        console.warn("genWelcomeMessagesFor: skipping device:", dev._id, "missing kyber key");
        continue;
      }
      if (rtree.pki !== GroupPKI.crystals && !dev.publicKey) {
        console.warn("genWelcomeMessagesFor: skipping device:", dev._id, "missing public key");
        continue;
      }
      if (dev._id === myDeviceID) { continue; }
      const frame = await this.welcomeMessage(rtree, dev, accountID, myUserID, myDeviceID,
        delegate.selfKeys, unencrypted, MessageFrameType.WELCOME);
      yield [dev, frame];
    }
  }

  async reWelcomeMessagesFor(rtree: RatchetTree, devices: DeviceNode[],
    myUserID: string, myDeviceID: string, delegate: ProtocolServiceDelegate): Promise<MessageFrame[]> {
    if (!rtree.accountID) {
      throw Error("Missing accountID on group");
    }
    const unencrypted = this.serializeGroup(rtree, myDeviceID);
    return bluebird.map(devices, dev => {
      return this.welcomeMessage(rtree, dev, rtree.accountID, myUserID, myDeviceID,
        delegate.selfKeys, unencrypted, MessageFrameType.REWELCOME);
    });
  }

  async *genReWelcomeMessagesFor(rtree: RatchetTree, accountID: string, devices: Array<DeviceNode>,
    myUserID: string, myDeviceID: string, delegate: ProtocolServiceDelegate): AsyncGenerator<[DeviceNode, MessageFrame], void, void> {
    const unencrypted = this.serializeGroup(rtree, myDeviceID);
    for (const dev of devices) {
      if (rtree.pki === GroupPKI.crystals && !dev.kyberKey) {
        console.warn("genReWelcomeMessagesFor: skipping device:", dev._id, "missing kyber key");
        continue;
      }
      if (rtree.pki !== GroupPKI.crystals && !dev.publicKey) {
        console.warn("genReWelcomeMessagesFor: skipping device:", dev._id, "missing public key");
        continue;
      }
      if (dev._id === myDeviceID) { continue; }
      const frame = await this.welcomeMessage(rtree, dev, accountID, myUserID, myDeviceID,
        delegate.selfKeys, unencrypted, MessageFrameType.REWELCOME);
      yield [dev, frame];
    }
  }

  externalWelcomeFor(rtree: RatchetTree, device: { publicKey?: string, kyberKey?: string }, thisDevice: string): MessageFrame {
    if (!rtree.accountID) {
      throw new Error("Missing account ID on Group");
    }
    const publicKey = rtree.pki === GroupPKI.crystals ? device.kyberKey : device.publicKey;
    if (!publicKey) {
      throw new Error("no public key for device");
    }
    const myIndex = rtree.indexOfDevice(thisDevice);
    const myNode = rtree.treenodes[myIndex];

    const signKey = rtree.pki === GroupPKI.crystals ? myNode.signKey : myNode.privateKey;
    if (!signKey) {
      throw new Error("no private signing key for this device's tree node");
    }

    const resumptions = rtree.resumption_secrets.map(r => {
      return { epoch: r.epoch, secret: r.secret };
    });
    const privateKeys: Array<{ i: number, priv: string }> = [];
    for (let i = 0; i < rtree.treenodes.length; i++) {
      if (i % 2 === 0) { continue; }
      const node = rtree.treenodes[i];
      //expect(node.privateKey, `user: ${rtree.desc} i: ${i}`).toBeTruthy();
      if (!node.privateKey) { continue; } // make TS happy
      privateKeys.push({ i: i, priv: node.privateKey });
    }

    const group_keys = rtree.group_keys ? Array.from(rtree.group_keys) : undefined;
    const welMsg: ExternalWelcomeMessage = {
      resumption_secrets: resumptions,
      data_secrets: rtree.data_secrets,
      private_keys: privateKeys,
      group_keys: group_keys
    };

    const buf = this.packMessagePayload(welMsg);

    const frame = this.frameSignedBy(
      rtree, rtree.accountID, myNode.userID as string, thisDevice,
      MessageFrameType.EXT_WELCOME, buf, rtree.epoch, rtree.msgCounter, myIndex,
      publicKey, signKey
    );
    return frame;
  }


  async sendFromGenerator(gen: AsyncGenerator<[DeviceNode, MessageFrame], void, void>,
    rtree: RatchetTree, delegate: ProtocolServiceDelegate) {
    let frames: Array<[DeviceNode, MessageFrame]> = [];
    for await (const pair of gen) {
      frames.push(pair);

      if (frames.length > 50) {
        await bluebird.map(frames, ([device, frame]) => {
          return delegate.sendFrameTo(rtree.accountID, rtree._id, device._id, frame)
        });
        frames = [];
      }
    }
    if (frames.length > 0) {
      await bluebird.map(frames, ([device, frame]) => {
        return delegate.sendFrameTo(rtree.accountID, rtree._id, device._id, frame)
      });
    }
    //console.log("sent", frames.length, "frames");
  }

  async createRatchetTree(group: Group, devices: Array<DeviceNode>, delegate?: ProtocolServiceDelegate): Promise<RatchetTree> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    if (!group.accountID) { throw new Error("Missing accountID on group"); }
    const rtree = await this.doRatchetTreeCreation(group, delegate.selfDeviceID, devices);

    const gen = this.genWelcomeMessagesFor(rtree, group.accountID, devices,
      delegate.selfUserID, delegate.selfDeviceID, delegate);

    await this.sendFromGenerator(gen, rtree, delegate);

    return rtree;
  }

  async doRatchetTreeCreation(group: Group, myDeviceID: string,
    devices: Array<DeviceNode>): Promise<RatchetTree> {
    const rtree = new RatchetTree(group);

    for (const dev of devices) {
      if (rtree.pki === GroupPKI.crystals && !dev.kyberKey ||
        rtree.pki !== GroupPKI.crystals && !dev.publicKey) {
        console.warn("missing needed key on device:", (dev as any).desc);
        continue;
      }
      this.treeService.addDeviceToGroup(rtree, dev, undefined, true);
    }

    const [init_secret, data_secret] = await bluebird.all([
      this.cryptoService.generateRandSecret(),
      this.cryptoService.generateRandSecret()
    ]);
    rtree.data_secrets = [{ _id: new ObjectId().toString(), secret: data_secret }];

    const myIndex = rtree.indexOfDevice(myDeviceID);
    if (myIndex < 0) {
      console.error("Couldn't find index of", myDeviceID, "in group:", rtree);
      throw Error("Couldn't find index of this device in group");
    }

    await this.ratchetEpoch(rtree, myIndex, init_secret);
    rtree.welcomed = true;

    return rtree;
  }

  async rewelcome(rtree: RatchetTree, oldTree: RatchetTree | null,
    devices: Array<DeviceNode>, protoVersion: number, delegate?: ProtocolServiceDelegate): Promise<RatchetTree> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    if (!rtree.accountID) { throw new Error("Missing accountID on group"); }

    rtree.protoVersion = protoVersion;
    this.treeService.updateMsgCounter(rtree, rtree.msgCounter);
    await this.doRewelcome(rtree, oldTree, devices, delegate.selfDeviceID);

    const gen = this.genReWelcomeMessagesFor(rtree, rtree.accountID, devices,
      delegate.selfUserID, delegate.selfDeviceID, delegate);

    await this.sendFromGenerator(gen, rtree, delegate);
    return rtree;
  }

  async doRewelcome(rtree: RatchetTree, oldTree: RatchetTree | null, devices: Array<DeviceNode>, thisDevice: string): Promise<void> {
    rtree.treenodes = [];

    if (oldTree) {
      rtree.resumption_secrets = oldTree.resumption_secrets;
      rtree.data_secrets = oldTree.data_secrets;
      rtree.group_keys = oldTree.group_keys;
    }

    for (const device of devices) {
      if (rtree.pki === GroupPKI.crystals && !device.kyberKey) {
        console.warn("skipping device:", device._id, "missing kyberKey key");
        continue;
      }
      if (rtree.pki !== GroupPKI.crystals && !device.publicKey) {
        console.warn("skipping device:", device._id, "missing public key");
        continue;
      }
      this.treeService.addDeviceToGroup(rtree, device, undefined, true);
    }
    const myIndex = rtree.indexOfDevice(thisDevice);
    if (myIndex < 0) {
      console.error("Couldn't find own index of", thisDevice, "in group:", rtree);
      throw Error("Couldn't find index of this device in new group");
    }
    const init_secret = await this.cryptoService.generateRandSecret();
    await this.ratchetEpoch(rtree, myIndex, init_secret);
  }

  /*async processWelcome(group: Group, thisDevice: string, keys: Keys): Promise<RatchetTree> {
    const rtree = new RatchetTree(group);
    const myIndex = rtree.indexOfDevice(thisDevice);
    const myNode = rtree.treenodes[myIndex];
    if (!myNode.publicKey) {
      console.error("My node is missing a publicKey:", myNode, "group:", rtree._id);
    } else if (typeof myNode.publicKey === "string") {
      const publicKey = keys.publicKey;
      if (myNode.publicKey !== publicKey) {
        console.warn("Mismatched publicKeys on myNode, me", thisDevice,
          "my key:", publicKey, "on node:", myNode.publicKey);
      }
    }
    myNode.privateKey = keys.privateKey;

    return rtree;
  }*/

  async join(group: Group, device: DeviceNode, delegate?: ProtocolServiceDelegate): Promise<RatchetTree> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    const rtree = new RatchetTree(group);
    if (!rtree.accountID) { throw new Error("Account ID missing on group"); }
    if (!rtree.externalPubKey) { throw new Error("This group did not publish a joining key"); }

    // pub key changes once we do keyScheduleDerivation
    const pubKey = rtree.externalPubKey;
    const msg = await this.doExternalJoin(rtree,
      delegate.selfUserID, device, delegate.selfKeys);

    const myIndex = rtree.indexOfDevice(delegate.selfDeviceID);
    const frame = this.frameSignedBy(rtree, rtree.accountID,
      delegate.selfUserID,
      delegate.selfDeviceID,
      MessageFrameType.EXT_ADD,
      this.packMessagePayload(msg),
      group.epoch,
      group.msgCounter,
      myIndex,
      pubKey,
      delegate.selfKeys);

    await delegate.sendFrame(rtree.accountID, rtree._id, frame);

    this.treeService.updateMsgCounter(rtree, frame.msgCounter);

    return rtree;
  }

  async doExternalJoin(rtree: RatchetTree, myUserID: string, thisDevice: DeviceNode, keys: Keys): Promise<AddDevicesMessage> {
    if (!rtree.accountID) {
      throw new Error("Missing accountID on group");
    }

    let addedDevice = false;
    let myIndex = rtree.indexOfDevice(thisDevice._id);
    if (myIndex < 0) {
      const myNode = this.makeTreeNode(myUserID, thisDevice._id, rtree, keys);
      myIndex = rtree.addLeaf(myNode);
      addedDevice = true;
    }
    rtree.welcomed = false;

    const init_secret = await this.cryptoService.generateRandSecret();
    const [updatePath, treeHash, devHash] = await this.ratchetEpoch(rtree, myIndex, init_secret);

    rtree.resumption_secrets = [];
    rtree.data_secrets = [];
    rtree.group_keys = undefined; // we don't want to save the group_key when joining, we don't have it yet
    rtree.welcomed = false;

    const msg: AddDevicesMessage = {
      updatePath: updatePath,
      devices: addedDevice ? [thisDevice] : [],
      parents: addedDevice ? [rtree.treenodes[rtree.parentIndex(myIndex)]] : [],
      init_secret: init_secret,
      treeHash: treeHash,
      derivationHash: devHash
    };

    return msg;
  }

  async processExternalJoin(rtree: RatchetTree,
    device: DeviceNode | undefined, parent: RatchetTreeNode | undefined,
    init_secret: Uint8Array,
    updatePath: Array<UpdatePathNode>,
    senderIndex: number,
    expectedTreeHash: string,
    expectedDerivationHash: string,
    thisDevice: string) {
    if (device) {
      if (rtree.pki === GroupPKI.crystals && !device.kyberKey) {
        console.error("No kyber key available for device:", device);
        return;
      }
      if (rtree.pki !== GroupPKI.crystals && !device.publicKey) {
        console.error("No public key available for device:", device);
        return;
      }
    }
    const myIndex = rtree.indexOfDevice(thisDevice);
    if (myIndex < 0) {
      console.error("Couldn't find my node in group:", rtree._id, thisDevice);
      throw new Error("This device isn't a member of the group");
    }

    if (device) {
      this.treeService.addDeviceToGroup(rtree, device, parent);
      if (rtree.indexOfDevice(device._id) !== senderIndex) {
        throw new Error("Mismatched indices of joining member");
      }
    }

    const [treeHash, devHash] = await this.ratchetEpochFromUpdate(rtree, updatePath,
      myIndex, senderIndex, init_secret);

    if (expectedTreeHash !== treeHash) {
      console.error("mismatched hashes, reported:", expectedTreeHash, "computed:", treeHash);
      reportErrorInfo({
        message: "tree hash mismatch",
        error: `group: ${rtree._id} device: ${thisDevice}`,
        level: "warning"
      });
    }

    if (expectedDerivationHash !== devHash) {
      console.error("mismatched derivation hashes, reported:", expectedDerivationHash,
        "computed:", devHash);
      reportErrorInfo({
        message: "derivation hash mismatch",
        error: `group: ${rtree._id} device: ${thisDevice}`,
        level: "error"
      });
    }
  }

  processExternalWelcome(rtree: RatchetTree,
    resumption_secrets: Array<{ epoch: number, secret: Uint8Array }>,
    data_secrets: Array<{ _id: string, secret: Uint8Array }>,
    private_keys: Array<{ i: number, priv: string }>) {
    rtree.resumption_secrets = resumption_secrets;
    rtree.data_secrets = data_secrets;
    if (private_keys) {
      for (const entry of private_keys) {
        const node = rtree.treenodes[entry.i];
        if (node.privateKey) {
          if (node.privateKey !== entry.priv) {
            console.error("mismatching private keys on node:", entry.i, "group:", rtree._id);
            reportErrorInfo({
              message: "extadd_priv_key_mismatch",
              error: `node: ${entry.i} group: ${rtree._id}`,
              level: "error"
            });
          }
        } else {
          node.privateKey = entry.priv;
        }
      }
    }
    rtree.outOfSync = false;
    rtree.welcomed = true;
  }

  async recover(rtree: RatchetTree, oldTree: RatchetTree, delegate?: ProtocolServiceDelegate): Promise<RatchetTree> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    if (!rtree.accountID) { throw new Error("Account ID missing on group"); }
    if (!rtree.externalPubKey) { throw new Error("This group did not publish a recovery key"); }

    const myIndex = rtree.indexOfDevice(delegate.selfDeviceID);
    const epoch = rtree.epoch;
    const externalPubKey = rtree.externalPubKey;
    const msg = await this.doRecovery(rtree, oldTree,
      delegate.selfDeviceID, delegate.selfKeys);

    const frame = this.frameSignedBy(rtree, rtree.accountID,
      delegate.selfUserID,
      delegate.selfDeviceID,
      MessageFrameType.RECOVER,
      this.packMessagePayload(msg),
      epoch,
      rtree.msgCounter,
      myIndex,
      externalPubKey,
      delegate.selfKeys);

    await delegate.sendFrame(rtree.accountID, rtree._id, frame);

    this.treeService.updateMsgCounter(rtree, rtree.msgCounter);
    return rtree;
  }

  async doRecovery(rtree: RatchetTree, oldTree: RatchetTree,
    thisDevice: string, keys: Keys): Promise<RecoverMessage> {
    if (!rtree.accountID) {
      throw new Error("Missing accountID on group");
    }
    const myIndex = rtree.indexOfDevice(thisDevice);
    const myNode = rtree.treenodes[myIndex];
    if (!myNode) {
      console.error("This device:", thisDevice, "doesn't exist in group:", rtree);
      throw new RecoveryTooOldError("This device does not exist in group");
    }

    myNode.publicKey = keys.publicKey;
    myNode.privateKey = keys.privateKey;

    //console.log("group:", group, "faultyGroup:", faultyGroup, faultyGroup.resumption_secrets);
    const init_secret = await this.cryptoService.generateRandSecret();
    const recoverData = oldTree.resumption_secrets[oldTree.resumption_secrets.length - 1];

    if (rtree.epoch - recoverData.epoch > MAX_RESUMPTION_COUNT) {
      console.error("Our group is two many epochs behind to recover, ours:",
        recoverData.epoch, "server:", rtree.epoch);
      throw new RecoveryTooOldError("Cannot automatically Recover");
    }

    const [updatePath, treeHash, devHash] = await this.ratchetEpoch(
      rtree, myIndex, init_secret, recoverData.secret);

    rtree.data_secrets = oldTree.data_secrets;
    rtree.resumption_secrets = oldTree.resumption_secrets.concat(rtree.resumption_secrets);

    const msg: RecoverMessage = {
      updatePath: updatePath,
      resumeFromEpoch: recoverData.epoch,
      init_secret: init_secret,
      treeHash: treeHash,
      derivationHash: devHash
    };

    return msg;
  }

  async processRecovery(rtree: RatchetTree,
    fromDevice: DeviceNode,
    atEpoch: number,
    init_secret: Uint8Array,
    updatePath: Array<UpdatePathNode>,
    expectedSenderIndex: number,
    expectedTreeHash: string,
    expectedDerivationHash: string,
    thisDevice: string): Promise<void> {

    const senderIndex = rtree.indexOfDevice(fromDevice._id);
    const senderNode = rtree.treenodes[senderIndex];
    if (senderNode.userID !== fromDevice.userID) {
      console.error("Recover: Device does not belong to tree node:", fromDevice, senderNode);
      throw new Error("Mismatching devices");
    }
    if (senderIndex !== expectedSenderIndex) {
      console.error("Sender index found in tree from deviceID:", fromDevice._id,
        "i:", senderIndex, "doesn't match senderData.sender:", expectedSenderIndex);
      throw new Error("Mismatched sending information");
    }

    const myIndex = rtree.indexOfDevice(thisDevice);
    const recoverData = rtree.resumption_secrets.find(r => r.epoch === atEpoch);
    if (!recoverData) {
      console.error("Couldn't find resumption_secret for prior epoch:", atEpoch);
      throw new Error("Missing resumption secret for recovery");
    }

    // console.log("resumption_secret:", bufferToBase64(recoverData.secret));

    const [treeHash, devHash] = await this.ratchetEpochFromUpdate(rtree, updatePath, myIndex,
      senderIndex, init_secret, recoverData.secret);

    if (expectedTreeHash !== treeHash) {
      console.error("mismatched hashes, reported:", expectedTreeHash, "computed:", treeHash);
    }
    if (expectedDerivationHash !== devHash) {
      console.error("mismatched derivation hashes, reported:", expectedDerivationHash,
        "computed:", devHash);
    }
  }

  async addMembers(rtreeOrGroupID: RatchetTree | string, devices: Array<DeviceNode>, delegate?: ProtocolServiceDelegate): Promise<RatchetTree> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    const rtree = typeof rtreeOrGroupID === "string" ? await delegate.getRatchetTree(rtreeOrGroupID) : rtreeOrGroupID;
    if (!rtree) { throw new GroupError("Encryption Keys For Group Not Found", 400); }
    if (!rtree.accountID) { throw new Error("Account ID missing on group"); }

    const updatedTree = RatchetTree.clone(rtree);
    const msg = await this.doAddMembers(updatedTree, devices, delegate.selfDeviceID);
    const frame = await this.frameForData(rtree, delegate.selfUserID, delegate.selfDeviceID,
      MessageFrameType.ADD_MEMBERS, this.packMessagePayload(msg), "handshake_secret", "commit");

    await delegate.sendFrame(rtree.accountID, rtree._id, frame);

    this.treeService.updateMsgCounter(updatedTree, frame.msgCounter);

    const frames = this.welcomeMessagesFor(updatedTree, devices,
      delegate.selfUserID, delegate.selfDeviceID, delegate);
    await bluebird.map(frames, (frame, i) => {
      const device = devices[i];
      return delegate?.sendFrameTo(rtree.accountID, rtree._id, device._id, frame);
    });

    return updatedTree;
  }

  async doAddMembers(rtree: RatchetTree, devices: Array<DeviceNode>, thisDevice: string): Promise<AddDevicesMessage> {
    const thisIndex = rtree.indexOfDevice(thisDevice);
    if (thisIndex < 0) {
      throw new Error("This browser isn't a member of this group. Cannot add new members!");
    }
    // The server said we were good, start modifying the ratchet tree
    const parents: Array<RatchetTreeNode> = [];
    for (const device of devices) {
      const [parent,] = this.treeService.addDeviceToGroup(rtree, device, undefined, true);
      // clone the parent so that the hash below isn't included in AddDevicesMessage
      parents.push(this.treeService.cloneTreeNode(parent as RatchetTreeNode));
    }
    const init_secret = rtree.next_init_secret || await this.cryptoService.generateRandSecret();
    const [updatePath, treeHash, devHash] = await this.ratchetEpoch(rtree, thisIndex, init_secret);

    const msg: AddDevicesMessage = {
      updatePath: updatePath,
      devices: devices,
      parents: parents,
      treeHash: treeHash,
      derivationHash: devHash
    };

    return msg;
  }

  async processAddMembers(
    rtree: RatchetTree,
    devices: Array<DeviceNode>, parents: Array<RatchetTreeNode>,
    updatePath: Array<UpdatePathNode>,
    senderIndex: number,
    expectedTreeHash: string,
    thisDevice: string): Promise<RatchetTree> {

    //const senderNode = rtree.treenodes[senderIndex];
    for (let i = 0; i < devices.length; i++) {
      this.treeService.addDeviceToGroup(rtree, devices[i], parents[i]);
    }

    const myIndex = rtree.indexOfDevice(thisDevice);
    if (myIndex < 0) {
      console.error("Couldn't find my node in group:", rtree._id, thisDevice);
      throw new Error("This device isn't a member of the group");
    }

    const [treeHash,] = await this.ratchetEpochFromUpdate(
      rtree, updatePath, myIndex, senderIndex, rtree.next_init_secret as Uint8Array);

    if (expectedTreeHash !== treeHash) {
      console.error("mismatched hashes, reported:", expectedTreeHash, "computed:", treeHash);
    }
    rtree.outOfSync = false;
    return rtree;
  }

  async removeMember(rtreeOrGroupID: RatchetTree | string, deviceID: string, delegate?: ProtocolServiceDelegate): Promise<[RatchetTree, boolean]> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    const rtree = typeof rtreeOrGroupID === "string" ? await delegate.getRatchetTree(rtreeOrGroupID) : rtreeOrGroupID;
    if (!rtree) { throw new GroupError("Encryption Keys For Group Not Found", 400); }
    if (!rtree.accountID) { throw new Error("Account ID missing on group"); }

    const updatedTree = RatchetTree.clone(rtree);

    const [msg, removingSelf] = await this.doRemoveMember(updatedTree, deviceID, delegate.selfDeviceID);
    if (!msg && removingSelf) {
      return [updatedTree, removingSelf];
    }
    const frame = await this.frameForData(rtree, delegate.selfUserID, delegate.selfDeviceID,
      MessageFrameType.REMOVE_MEMBER, this.packMessagePayload(msg), "handshake_secret", "commit");

    await delegate.sendFrame(rtree.accountID, rtree._id, frame);

    this.treeService.updateMsgCounter(updatedTree, frame.msgCounter);

    return [updatedTree, removingSelf];
  }

  async doRemoveMember(rtree: RatchetTree, deviceID: string, thisDevice: string): Promise<[RemoveMemberMessage | null, boolean]> {
    const myIndex = rtree.indexOfDevice(thisDevice);
    const myNode = rtree.treenodes[myIndex];
    //const signingKey = myNode.privateKey; // changes once we call doTreeUpdatePath

    if (!myNode) {
      console.error("This device:", thisDevice, "doesn't exist in group:", rtree);
      throw new Error("This device does not exist in group");
    }

    const removeIndex = rtree.indexOfDevice(deviceID);
    if (removeIndex < 0) {
      console.error("Device:", deviceID, "doesn't exist in group:", rtree);
    }

    const removingSelf = rtree.treenodes[removeIndex].deviceID === thisDevice;
    if (removingSelf && rtree.treenodes.length === 1) {
      return [null, removingSelf];
    }

    const [oldRootNode, displacedIndex, displacedNode] =
      this.treeService.prepareForRemoveAtIndex(rtree, removeIndex);
    const [updatePath, commit_secret] = await this.treeService.doTreeUpdatePath(
      rtree, removeIndex);

    if (displacedNode) {
      rtree.treenodes[displacedIndex] = displacedNode;
    }
    const init_secret = rtree.next_init_secret || await this.cryptoService.generateRandSecret();

    const [treeHash, devHash] = await bluebird.all([
      this.treeService.computeTreeNodeHash(rtree),
      this.treeService.computeKeyDerivationHash(init_secret, commit_secret)
    ]);

    const msg: RemoveMemberMessage = {
      removed: removeIndex,
      updatePath: updatePath,
      treeHash: bufferToBase64(treeHash),
      derivationHash: bufferToBase64(devHash)
    };

    rtree.treenodes[removeIndex] = rtree.treenodes[rtree.treenodes.length - 1];
    rtree.treenodes.pop();
    rtree.treenodes.pop();

    await this.treeService.recomputeNodeHashes(rtree, removeIndex);

    const encryption_secret = await this.treeService.keyScheduleDerivation(
      rtree, init_secret, commit_secret, rtree.epoch + 1);

    //console.log("building ratchets with encryption_secret:",
    //  bufferToBase64(encryption_secret), "init:", bufferToBase64(init_secret));

    await this.treeService.computeSecretTree(rtree, rtree.rootIndex, undefined, encryption_secret);

    oldRootNode.privateKey = undefined;
    rtree.epoch++;

    return [msg, removingSelf];
  }

  async processRemoveMember(rtree: RatchetTree,
    updatePath: Array<UpdatePathNode>,
    removeIndex: number,
    expectedTreeHash: string,
    thisDevice: string) {
    if (removeIndex % 2 !== 0) {
      console.error("can only remove leaf nodes from tree, got:", removeIndex);
      return;
    }
    const nodeToRemove = rtree.treenodes[removeIndex];
    if (!nodeToRemove) {
      console.error("Remove Member: index:", removeIndex, "does not exist in group",
        rtree._id);
      return;
    }
    if (nodeToRemove.deviceID === thisDevice) { return; }

    const myOldIndex = rtree.indexOfDevice(thisDevice);
    if (myOldIndex < 0) {
      console.error("Couldn't find my node in group:", rtree._id, thisDevice);
      throw new Error("This device isn't a member of the group");
    }

    const [oldRootNode, displacedIndex, displacedNode] =
      this.treeService.prepareForRemoveAtIndex(rtree, removeIndex);

    // was my node the one to move? Then I'm in removeIndex now for this updatePath
    //const myIndex = myOldIndex === group.treenodes.length - 1 ? removeIndex : myOldIndex;

    // act like the updatePath was sent from removeIndex;
    const [updateNode, path_secret] = this.treeService.decryptUpdatePath(
      rtree, updatePath, removeIndex, removeIndex);

    const commit_secret = await this.treeService.fillTreeSecrets(rtree,
      updatePath, updateNode.index, path_secret, updateNode.publicKey);

    if (displacedNode) {
      rtree.treenodes[displacedIndex] = displacedNode;
    }

    const treeHash = bufferToBase64(await this.treeService.computeTreeNodeHash(rtree));
    if (expectedTreeHash !== treeHash) {
      console.error("mismatched hashes, reported:", expectedTreeHash, "computed:", treeHash);
    }

    rtree.treenodes[removeIndex] = rtree.treenodes[rtree.treenodes.length - 1];
    rtree.treenodes.pop();
    rtree.treenodes.pop();
    await this.treeService.recomputeNodeHashes(rtree, removeIndex);

    //console.log("removed last two nodes from group:", group);
    //console.log("commit_secret:", bufferToBase64(commit_secret));

    const init_secret = rtree.next_init_secret || await this.cryptoService.generateRandSecret();

    // update the the group and send the new secrets to the new user(s)
    const encryption_secret = await this.treeService.keyScheduleDerivation(
      rtree, init_secret, commit_secret, rtree.epoch + 1);

    //console.log("building ratchets with encryption_secret:",
    //  bufferToBase64(encryption_secret));

    await this.treeService.computeSecretTree(rtree, rtree.rootIndex, undefined, encryption_secret);

    oldRootNode.privateKey = undefined;
    rtree.epoch++;
    rtree.outOfSync = false;

    if (!rtree.treenodes[rtree.rootIndex].privateKey) {
      console.warn("Missing private key for ratchet tree root, index:", rtree.rootIndex);
    }
  }

  async setProtocolScheme(rtree: RatchetTree, protocolScheme: ProtocolScheme, delegate?: ProtocolServiceDelegate): Promise<RatchetTree> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }
    if (!rtree.accountID) { throw new Error("Account ID missing on group"); }
    if (!rtree.welcomed) {
      throw new Error("This device has not received all group secrets yet. Please, wait until another group device sends them.");
    }
    const thisIndex = rtree.indexOfDevice(delegate.selfDeviceID);
    if (thisIndex < 0) {
      throw new Error("This browser isn't a member of this group. Cannot add new members!");
    }

    const updatedTree = RatchetTree.clone(rtree);
    updatedTree.protocolScheme = protocolScheme;

    const init_secret = rtree.next_init_secret || await this.cryptoService.generateRandSecret();
    const [updatePath, treeHash, devHash] = await this.ratchetEpoch(updatedTree, thisIndex, init_secret);

    const newGroupKey = !rtree.group_keys && updatedTree.group_keys ||
      rtree.group_keys && updatedTree.group_keys && rtree.group_keys.length !== updatedTree.group_keys.length ?
      updatedTree.group_keys[updatedTree.group_keys.length - 1] : undefined;

    const msg: SchemeChangeMessage = {
      protocolScheme: protocolScheme,
      group_key: newGroupKey,
      updatePath: updatePath,
      treeHash: treeHash,
      derivationHash: devHash
    }

    const frame = await this.frameForData(rtree, delegate.selfUserID, delegate.selfDeviceID,
      MessageFrameType.SCHEME_CHANGE, this.packMessagePayload(msg), "handshake_secret", "commit");

    await delegate.sendFrame(rtree.accountID, rtree._id, frame);

    this.treeService.updateMsgCounter(updatedTree, frame.msgCounter);

    return updatedTree;
  }

  async processSchemeChange(rtree: RatchetTree,
    protocolScheme: ProtocolScheme,
    group_key: Uint8Array | undefined,
    updatePath: Array<UpdatePathNode>,
    senderIndex: number,
    expectedTreeHash: string,
    thisDevice: string): Promise<RatchetTree> {

    rtree.protocolScheme = protocolScheme;
    if (group_key) {
      if (!rtree.group_keys) { rtree.group_keys = []; }
      let notfound = true;
      for (const key of rtree.group_keys) {
        if (key === group_key) {
          notfound = false;
          break;
        }
      }
      if (notfound) {
        rtree.group_keys.push(group_key);
      }
    }

    const myIndex = rtree.indexOfDevice(thisDevice);
    if (myIndex < 0) {
      console.error("Couldn't find my node in group:", rtree._id, thisDevice);
      throw new Error("This device isn't a member of the group");
    }
    const [treeHash,] = await this.ratchetEpochFromUpdate(
      rtree, updatePath, myIndex, senderIndex, rtree.next_init_secret as Uint8Array);

    if (expectedTreeHash !== treeHash) {
      console.error("mismatched hashes, reported:", expectedTreeHash, "computed:", treeHash);
    }
    rtree.outOfSync = false;
    return rtree;
  }

  // for special message types
  frameSignedBy(rtree: RatchetTree, accountID: string, myUserID: string, thisDevice: string,
    frameType: MessageFrameType,
    data: Uint8Array,
    epoch: number,
    msgCounter: number,
    sendingIndex: number,
    sealingKey: string,
    signingKey: string | Keys): MessageFrame {

    const sendingNode = rtree.treenodes[sendingIndex];
    if (!sendingNode) {
      throw new Error("Node at index:" + sendingIndex + " not found in group");
    }
    const senderData: SenderData = {
      sender: sendingIndex,
      generation: sendingNode.handshakeGeneration,
      reuseGuard: new Uint8Array(0),
      senderID: myUserID,
      deviceID: thisDevice
    };

    const packedSender = this.packMessagePayload(senderData);

    let encrypted: Uint8Array;
    let encryptedSender: Uint8Array;
    if (rtree.pki === GroupPKI.crystals) {
      const publicKey = this.cryptoService.importKyberPubKey(sealingKey);
      encrypted = this.cryptoService.kyberEncrypt(data, publicKey);
      encryptedSender = this.cryptoService.kyberEncrypt(packedSender, publicKey);
      publicKey.delete();
    } else {
      const publicKey = this.cryptoService.importPublicKey(sealingKey);
      encrypted = this.cryptoService.encapsulate(data, publicKey);
      encryptedSender = this.cryptoService.encapsulate(packedSender, publicKey);
    }

    const frame: MessageFrame = {
      _id: new ObjectId().toHexString(),
      cmd: frameType,
      accountID: accountID,
      groupID: rtree._id,
      fromUserID: myUserID,
      fromDeviceID: thisDevice,
      created: new Date(),
      epoch: epoch,
      msgCounter: msgCounter,
      data: encrypted,
      senderData: encryptedSender,
      pki: rtree.pki,
      version: rtree.protoVersion
    };

    const sigData = this.treeService.makeSignatureData(frame);
    if (rtree.pki === GroupPKI.crystals) {
      const signKey = this.cryptoService.importDilithiumPriKey(
        typeof signingKey === "string" ? signingKey : signingKey.diPrivateKey);
      frame.signature = this.cryptoService.dilithiumSign(sigData, signKey);
      signKey.delete();
    } else {
      const signKey = this.cryptoService.importPrivateKey(
        typeof signingKey === "string" ? signingKey : signingKey.privateKey);
      frame.signature = this.cryptoService.sign(sigData, signKey);
    }

    // console.log("sender secret:", bufferToBase64(rtree.sender_data_secret));
    // console.log("sample", bufferToBase64(sample));
    // console.log("encrypting sender, key:", bufferToBase64(senderKeys.key));
    // console.log("nonce:", bufferToBase64(senderKeys.nonce));
    // console.log("context:", bufferToBase64(contextAAD));
    // console.log("guard:", bufferToBase64(keydata.guard));

    return frame;
  }

  async frameForDataSharedSecret(rtree: RatchetTree, myUserID: string, thisDeviceID: string,
    myKeys: Keys,
    frameType: MessageFrameType,
    data: Uint8Array,
    userIDsToNotify?: Array<NotifyUser>): Promise<MessageFrame> {
    if (!rtree || !rtree.treenodes) {
      console.error("No group on group:", rtree);
      throw new Error("Missing Group Encryption Information");
    }
    if (!rtree.group_keys || rtree.group_keys.length <= 0) {
      console.error("Missing group_key on group:", rtree);
      throw new Error("Missing Group Encryption Information");
    }

    const frameID = new ObjectId().toHexString();
    const groupID = rtree._id;
    const accountID = rtree.accountID;
    const group_key = rtree.group_keys[rtree.group_keys.length - 1];
    const keyidx = rtree.group_keys.length - 1;

    const contextAAD = rtree.groupContext("application");

    const frameBuf = hexToBuffer(frameID);
    const keydata = await this.treeService.computeKeyAndNonceFor(
      group_key, frameBuf, null);

    const encrypted = await this.cryptoService.seal(
      data, keydata.key, keydata.nonce, contextAAD);

    const sample = encrypted.slice(0, 32);

    const senderSecret = await this.cryptoService.deriveKDFSecret(
      group_key, new ArrayBuffer(0), frameID);

    //console.log("sample:", bufferToBase64(sample), "guard:", bufferToBase64(keydata.guard));
    const senderKeys = await bluebird.props({
      key: this.cryptoService.deriveKDFSecret(senderSecret, sample, "key"),
      nonce: this.cryptoService.deriveKDFSecret(senderSecret, sample, "nonce")
    });

    const senderData: SenderData = {
      sender: 0,
      generation: 0,
      senderID: myUserID,
      deviceID: thisDeviceID,
      reuseGuard: keydata.guard
    }

    // console.log("group key:", bufferToBase64(group_key));
    // console.log("sender secret:", bufferToBase64(senderSecret));
    // console.log("sample", bufferToBase64(sample));
    // console.log("encrypting sender, key:", bufferToBase64(senderKeys.key));
    // console.log("nonce:", bufferToBase64(senderKeys.nonce));
    // console.log("context:", bufferToBase64(contextAAD));
    // console.log("guard:", bufferToBase64(keydata.guard));

    const encryptedSender = await this.cryptoService.seal(
      serialize(senderData), senderKeys.key, senderKeys.nonce, contextAAD);

    const frame: MessageFrame = {
      _id: frameID,
      cmd: frameType,
      accountID: accountID,
      groupID: groupID,
      fromUserID: myUserID,
      fromDeviceID: thisDeviceID,
      created: new Date(),
      epoch: rtree.epoch,
      msgCounter: rtree.msgCounter,
      data: encrypted,
      senderData: encryptedSender,
      pki: rtree.pki,
      scheme: rtree.protocolScheme,
      keyidx: keyidx,
      version: rtree.protoVersion
    };

    if (userIDsToNotify) {
      frame.notifyUsers = userIDsToNotify;
    }

    if (rtree.pki === GroupPKI.crystals) {
      if (myKeys.diPrivateKey) {
        const sigData = this.treeService.makeSignatureData(frame);
        const privKey = this.cryptoService.importDilithiumPriKey(myKeys.diPrivateKey);
        frame.signature = this.cryptoService.dilithiumSign(sigData, privKey);
        privKey.delete();
        // console.log("sending index:", myIndex, myNode);
        // console.log("sigData:", bufferToBase64(sigData));
        // console.log("signing pub:", myNode.verifyKey);
        // console.log("signing key:", privKey);
        // console.log("signature:", bufferToBase64(frame.signature));
      } else {
        throw new Error("Missing signKey on myNode, cannot sign");
      }
    } else {
      if (myKeys.privateKey) {
        const sigData = this.treeService.makeSignatureData(frame);
        const privKey = this.cryptoService.importPrivateKey(myKeys.privateKey);
        frame.signature = this.cryptoService.sign(sigData, privKey);
        // console.log("sending index:", myIndex, myNode);
        // console.log("sigData:", bufferToBase64(sigData));
        // console.log("signing pub:", myNode.publicKey);
        // console.log("signing key:", privKey);
        // console.log("signature:", bufferToBase64(frame.signature));
      } else {
        throw new Error("Missing privateKey on my Node, cannot sign");
      }
    }

    //console.log("shared frame:", frame, "sender:", senderData);
    return frame;
  }

  async frameForData(rtree: RatchetTree, myUserID: string, thisDevice: string,
    frameType: MessageFrameType,
    data: Uint8Array,
    ratchetProp: keyof Ratchet,
    contentType: "application" | "commit",
    userIDsToNotify?: Array<NotifyUser>): Promise<MessageFrame> {
    if (!rtree || !rtree.treenodes) {
      console.error("No group or treenodes on group:", rtree);
      throw new Error("Missing Group Encryption Information");
    }
    if (!rtree.sender_data_secret) {
      console.error("No sender_data_secret on group:", rtree);
      throw new Error("Missing Sender Data");
    }
    if (!rtree.accountID) {
      throw new Error("Missing accountID on Group");
    }

    const groupID = rtree._id;
    const accountID = rtree.accountID;

    const myIndex = rtree.indexOfDevice(thisDevice);
    const myNode = rtree.treenodes[myIndex];

    if (!myNode) {
      console.error("Couldn't find ratchet for dev:",
        thisDevice, "index:", myIndex, "in group:", rtree);
      throw new Error("Couldn't Find Ratchet in Group");
    }

    const ratchet_secret = myNode[ratchetProp];

    if (!ratchet_secret) {
      console.log(`no ${ratchetProp} on group:`, rtree);
      throw new Error("Missing ratchet secret for this device in this group");
    }
    const generation = ratchetProp === "ratchet_secret" ?
      myNode.generation : myNode.handshakeGeneration;

    const keydata = await this.treeService.computeKeyAndNonce(
      ratchet_secret, myIndex, generation);

    const contextAAD = rtree.groupContext(contentType);

    // console.log("encrypting data, key:", bufferToBase64(keydata.key),
    //   "nonce:", bufferToBase64(keydata.nonce),
    //   "gen:", generation, "index", myIndex, "guard:", bufferToBase64(keydata.guard),
    //   "context:", bufferToBase64(contextAAD), contentType);

    let encrypted: Uint8Array;

    if (frameType === MessageFrameType.REMOVE_MEMBER ||
      frameType === MessageFrameType.RECOVER) {
      encrypted = data;
    } else {
      encrypted = await this.cryptoService.seal(
        data, keydata.key, keydata.nonce, contextAAD);
    }
    await this.treeService.ratchetForward(rtree, myIndex, ratchetProp);

    const sample = encrypted.slice(0, 32);

    //console.log("sample:", bufferToBase64(sample), "guard:", bufferToBase64(keydata.guard));
    const senderKeys = await bluebird.props({
      key: this.cryptoService.deriveKDFSecret(rtree.sender_data_secret, sample, "key"),
      nonce: this.cryptoService.deriveKDFSecret(rtree.sender_data_secret, sample, "nonce")
    });

    const senderData: SenderData = {
      sender: myIndex,
      generation: generation,
      reuseGuard: keydata.guard,
      senderID: myUserID,
      deviceID: thisDevice
    }

    // console.log("sender secret:", bufferToBase64(rtree.sender_data_secret));
    // console.log("sample", bufferToBase64(sample));
    // console.log("encrypting sender, key:", bufferToBase64(senderKeys.key));
    // console.log("nonce:", bufferToBase64(senderKeys.nonce));
    // console.log("context:", bufferToBase64(contextAAD));
    // console.log("guard:", bufferToBase64(keydata.guard));

    const encryptedSender = await this.cryptoService.seal(
      serialize(senderData), senderKeys.key, senderKeys.nonce, contextAAD);

    const frame: MessageFrame = {
      _id: new ObjectId().toHexString(),
      cmd: frameType,
      accountID: accountID,
      groupID: groupID,
      fromUserID: myUserID,
      fromDeviceID: thisDevice,
      created: new Date(),
      epoch: rtree.epoch,
      msgCounter: rtree.msgCounter,
      data: encrypted,
      senderData: encryptedSender,
      pki: rtree.pki,
      version: rtree.protoVersion
    };

    if (userIDsToNotify) {
      frame.notifyUsers = userIDsToNotify;
    }

    if (rtree.pki === GroupPKI.crystals) {
      if (myNode.signKey) {
        const sigData = this.treeService.makeSignatureData(frame);
        const privKey = this.cryptoService.importDilithiumPriKey(myNode.signKey);
        frame.signature = this.cryptoService.dilithiumSign(sigData, privKey);
        privKey.delete();
        // console.log("sending index:", myIndex, myNode);
        // console.log("sigData:", bufferToBase64(sigData));
        // console.log("signing pub:", myNode.verifyKey);
        // console.log("signing key:", privKey);
        // console.log("signature:", bufferToBase64(frame.signature));
      } else {
        throw new Error("Missing signKey on myNode, cannot sign");
      }
    } else {
      if (myNode.privateKey) {
        const sigData = this.treeService.makeSignatureData(frame);
        const privKey = this.cryptoService.importPrivateKey(myNode.privateKey);
        frame.signature = this.cryptoService.sign(sigData, privKey);
        // console.log("sending index:", myIndex, myNode);
        // console.log("sigData:", bufferToBase64(sigData));
        // console.log("signing pub:", myNode.publicKey);
        // console.log("signing key:", privKey);
        // console.log("signature:", bufferToBase64(frame.signature));
      } else {
        throw new Error("Missing privateKey on my Node, cannot sign");
      }
    }

    return frame;
  }

  async processSenderData(msg: MessageFrame, decryptKey: ArrayBuffer, contextAAD: ArrayBuffer): Promise<SenderData> {
    const sample = msg.data.slice(0, 32);
    const senderKeys = await bluebird.props({
      key: this.cryptoService.deriveKDFSecret(decryptKey, sample, "key"),
      nonce: this.cryptoService.deriveKDFSecret(decryptKey, sample, "nonce")
    });

    // console.log("  sender secret:", bufferToBase64(decryptKey))
    // console.log("  sample:", bufferToBase64(sample));
    // console.log("  opening sender, key:", bufferToBase64(senderKeys.key));
    // console.log("  nonce:", bufferToBase64(senderKeys.nonce));
    // console.log("  context:", bufferToBase64(contextAAD));

    let buf: Uint8Array;

    try {
      buf = await this.cryptoService.open(
        msg.senderData, senderKeys.key, senderKeys.nonce, contextAAD);
    } catch (err) {
      console.error("Failed to decrypt sender data. Group likely out of sync!!", err);
      throw new GroupError("Failed to decrypt sender data", 400, ErrorCode.sync);
    }

    return deserialize(buf, { promoteBuffers: true, promoteValues: true }) as SenderData;
  }

  async processIncomingMessage(rtree: RatchetTree, frame: MessageFrame,
    ratchetProp: keyof Ratchet, contentType: "application" | "commit"): Promise<ProcessedMessage> {

    let buf: Uint8Array;

    if (!rtree.sender_data_secret) {
      console.error("missing sender_data_secret on group:", rtree);
      throw new Error("No sender_data_secret on group: " + rtree.groupname);
    }
    const contextAAD = rtree.groupContext(contentType, frame.epoch);

    const senderData = await this.processSenderData(
      frame, rtree.sender_data_secret, contextAAD);

    const senderIndex = senderData.sender;
    const senderNode = rtree.treenodes[senderIndex];

    if (!senderNode) {
      console.log("Missing node for senderIndex:", senderIndex, "group:", rtree._id);
      throw new Error("Missing sender node.");
    }
    if (!senderNode.publicKey) {
      console.log("Missing public key for sender:", senderIndex, "group:", rtree._id);
      throw new Error("Missing sender node's public key");
    }
    if (senderNode.deviceID !== frame.fromDeviceID) {
      console.log("Mismatch devices from sender node:", senderNode.deviceID,
        "msg:", frame.fromDeviceID, "group:", rtree._id);
      throw new Error("Declared device ID does not match sender's");
    }

    // Verify signature
    if (frame.signature) {
      this.verifyFrameSignature(frame, senderNode, rtree); // will throw if fails
    } else {
      console.warn("no signature on message frame");
    }

    let ratchet_secret = senderNode[ratchetProp];
    let generation = ratchetProp === "ratchet_secret" ?
      senderNode.generation : senderNode.handshakeGeneration;
    if (!ratchet_secret) {
      console.log("missing ratchet_secret on node:", senderNode);
      throw new Error("Missing ratchet for message sender");
    }
    if (generation > senderData.generation) {
      console.log("Mismatch at index:", senderIndex, "this gen:", generation,
        "reported:", senderData.generation);
      throw new GroupError("Mismatched Generation on Sender Node. We're out of sync!", 400, ErrorCode.sync);
    }
    for (let i = generation; i < senderData.generation; i++, generation++) {
      ratchet_secret = await this.treeService.ratchetForward(rtree, senderIndex, ratchetProp);
    }

    if (frame.cmd === MessageFrameType.REMOVE_MEMBER) {
      buf = frame.data;
    } else {
      const keydata = await this.treeService.computeKeyAndNonce(
        ratchet_secret, senderIndex, generation, senderData.reuseGuard);

      // console.log(" decrypting data with key:", bufferToBase64(keydata.key),
      //   "nonce:", bufferToBase64(keydata.nonce),
      //   "gen:", generation, "index:", senderIndex,
      //   "guard", bufferToBase64(keydata.guard),
      //   "context:", bufferToBase64(contextAAD));
      buf = await this.cryptoService.open(frame.data,
        keydata.key, keydata.nonce, contextAAD);
    }

    //console.log("msg counter:", msg.msgCounter, "group msgCounter:", group.msgCounter);
    this.treeService.updateMsgCounter(rtree, frame.msgCounter);
    await this.treeService.ratchetForward(rtree, senderIndex, ratchetProp);

    //printGroup(group);
    rtree.outOfSync = false;

    return { buf, senderNode, senderIndex };
  }

  async processIncomingWithSharedSecret(rtree: RatchetTree, frame: MessageFrame,
    contentType: "application" | "commit", isNew: boolean,
    delegate: ProtocolServiceDelegate): Promise<ProcessedSharedSecretMessage> {

    if (!rtree.group_keys || rtree.group_keys.length <= 0) {
      console.error("missing group_key on group:", rtree);
      throw new Error("No group_keys on group: " + rtree.groupname);
    }
    const contextAAD = rtree.groupContext(contentType, frame.epoch);
    const keyidx = frame.keyidx || rtree.group_keys.length - 1;
    const group_key = rtree.group_keys[keyidx];

    const senderSecret = await this.cryptoService.deriveKDFSecret(
      group_key, new ArrayBuffer(0), frame._id);

    const senderData = await this.processSenderData(
      frame, senderSecret, contextAAD);

    if (senderData.senderID !== frame.fromUserID) {
      console.log("Mismatch users from sender data:", senderData.senderID,
        "msg:", frame.fromUserID, "group:", rtree._id);
      throw new Error("Declared user ID does not match sender's");
    }
    if (senderData.deviceID !== frame.fromDeviceID) {
      console.log("Mismatch devices from sender data:", senderData.deviceID,
        "msg:", frame.fromDeviceID, "group:", rtree._id);
      throw new Error("Declared device ID does not match sender's");
    }

    const device = await delegate.getDevice(senderData.deviceID);

    // Verify signature
    if (frame.signature && device) {
      this.verifyFrameSignature(frame, device, rtree);
    } else {
      console.warn("no signature on message frame");
    }

    const frameBuf = hexToBuffer(frame._id);
    const keydata = await this.treeService.computeKeyAndNonceFor(
      group_key, frameBuf, senderData.reuseGuard);

    // console.log(" decrypting data with key:", bufferToBase64(keydata.key),
    //   "nonce:", bufferToBase64(keydata.nonce),
    //   "gen:", generation, "index:", senderIndex,
    //   "guard", bufferToBase64(keydata.guard),
    //   "context:", bufferToBase64(contextAAD));
    const buf = await this.cryptoService.open(frame.data,
      keydata.key, keydata.nonce, contextAAD);

    isNew && this.treeService.updateMsgCounter(rtree, frame.msgCounter);

    return { buf, senderUserID: senderData.senderID, senderDeviceID: senderData.deviceID };
  }

  processStoredMessageFrame(frame: MessageFrame, delegate?: ProtocolServiceDelegate): Promise<any> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }

    switch (frame.cmd) {
      case MessageFrameType.MESSAGE:
      case MessageFrameType.APP_BACKGROUND:
        return this.onStoredAppMessage(frame, delegate);
      default:
        console.error("unexpect message type:", frame.cmd, "in processStoredMessageFrame");
        return bluebird.resolve();
    }
  }

  async onStoredAppMessage(frame: MessageFrame, delegate: ProtocolServiceDelegate): Promise<any> {
    //console.log("onStoredAppMessage, group:", frame.groupID, frame);
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      console.warn("no group found for add members message.. maybe we're adding this device.");
      return null;
    }

    const { buf } = await this.processIncomingWithSharedSecret(rtree, frame, "application", false, delegate);

    const message = this.unpackMessagePayload(buf) as ContentMessage;
    await delegate.onAppHistoricalMessage(message, frame, rtree);
  }

  async processMessageFrame(frame: MessageFrame, delegate?: ProtocolServiceDelegate): Promise<any> {
    delegate = delegate || this.delegate;
    if (!delegate) { throw new Error("No protocol delegate supplied"); }

    switch (frame.cmd) {
      case MessageFrameType.MESSAGE:
      case MessageFrameType.APP_BACKGROUND:
        return this.onAppMessage(frame, delegate);

      case MessageFrameType.ADD_MEMBERS:
        return this.onAddMembers(frame, delegate);

      case MessageFrameType.WELCOME:
      case MessageFrameType.REWELCOME:
        return this.onWelcome(frame, delegate);

      case MessageFrameType.EXT_WELCOME:
        return this.onExtWelcome(frame, delegate);

      case MessageFrameType.EXT_ADD:
        return this.onExternalAdd(frame, delegate);

      case MessageFrameType.REMOVE_MEMBER:
        return this.onRemoveMember(frame, delegate);

      case MessageFrameType.RECOVER:
        return this.onRecoverGroup(frame, delegate);

      case MessageFrameType.REMOVE:
        return this.onDeleteGroup(frame, delegate);

      case MessageFrameType.REMOVE_MSG:
        return this.onRemoveMessage(frame, delegate);

      case MessageFrameType.REMOVE_REPLY:
        return this.onRemoveReply(frame, delegate);

      case MessageFrameType.NEW_DATA_KEY:
        return this.onDataKeyMessage(frame, delegate);

      case MessageFrameType.REMOVE_MEMBER_REQ:
        return delegate.processServerRequest(frame);

      case MessageFrameType.SCHEME_CHANGE:
        return this.onSchemeChange(frame, delegate);

      case MessageFrameType.CONTROL:
      case MessageFrameType.START_TYPING_CMD:
      case MessageFrameType.STOP_TYPING_CMD:
      case MessageFrameType.HUDDLE_START:
      case MessageFrameType.HUDDLE_END:
        return bluebird.resolve();

      default:
        console.error("Unknown message type:", frame.cmd);
        return this.onUnknownMessage(frame, delegate);
    }
  }

  async onWelcome(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    //console.log("Processing", frame.cmd === MessageFrameType.REWELCOME ? "REWELCOME" : "WELCOME",
      //"Msg:", frame.msgCounter);
    // Verify signature
    const [oldGroup, sendingDevice] = await bluebird.all([
      delegate.getRatchetTree(frame.groupID),
      delegate.getDevice(frame.fromDeviceID)
    ]);
    if (!sendingDevice) {
      throw new Error("Device of Sender not found");
    }

    // just for welcome messages, we can test on equal counters
    if (oldGroup && oldGroup.msgCounter >= frame.msgCounter) {
      console.warn("we already processed this msg on group:", oldGroup._id,
        "group msgCounter:", oldGroup.msgCounter, "msg:", frame.msgCounter);
      return;
    }
    if (!delegate.selfKeys || !delegate.selfKeys.privateKey) {
      throw new Error("onWelcome, missing private key for this device");
    }
    let group: Group;
    try {
      if (!frame.pki) { frame.pki = GroupPKI.p256; }
      group = this.decryptAndUnpack(frame.data, delegate.selfKeys, frame) as Group;
    } catch (e) {
      console.error("Failed to decrypt WELCOME data", e);
      throw new Error("Failed to decrypt WELCOME data");
    }
    if (!group.pki) { group.pki = GroupPKI.p256; }

    if (group.pki === GroupPKI.crystals && !sendingDevice.kyberKey) {
      throw new Error("No kyber Key for sending device from server");
    }
    if (group.pki !== GroupPKI.crystals && !sendingDevice.publicKey) {
      throw new Error("No public Key for sending device from server");
    }

    if (group.created) {
      if ((group.created as any)._d) {
        group.created = moment((group.created as any)._d);
      } else if (!moment.isMoment(group.created)) {
        group.created = moment(group.created);
      }
    }

    this.verifyFrameSignature(frame, sendingDevice, group as ProtocolVersioning); // will throw if fails

    if (!group._id) {
      group._id = frame.groupID;
    }
    if (typeof group._id !== "string") {
      group._id = (group._id as ObjectId).toString();
    }
    const gid = typeof frame.groupID === "string" ? frame.groupID :
      (frame.groupID as any).toString();

    if (!group._id || group._id !== gid) {
      throw new Error("Missing or mismatching group IDs");
    }
    delete group.lastMsgTime;

    const rtree = new RatchetTree(group);
    const index = rtree.indexOfDevice(delegate.selfDeviceID);
    if (index < 0) {
      throw new Error("Missing this device in new group!");
    }
    const myNode = rtree.treenodes[index];

    if (!myNode.publicKey) {
      console.error("My node is missing a publicKey:", myNode, "group:", rtree._id);
    } else if (typeof myNode.publicKey === "string") {
      const publicKey = group.pki === GroupPKI.crystals ?
        delegate.selfKeys.kyberPublicKey :
        delegate.selfKeys.publicKey;
      if (myNode.publicKey !== publicKey) {
        console.warn("Mismatched publicKeys on myNode, me", delegate.selfDeviceID,
          "my key:", publicKey, "on node:", myNode.publicKey);
      }
    }
    if (group.pki === GroupPKI.crystals) {
      myNode.privateKey = delegate.selfKeys.kyberPrivateKey;
      myNode.signKey = delegate.selfKeys.diPrivateKey;
    } else {
      myNode.privateKey = delegate.selfKeys.privateKey;
    }

    if (oldGroup) {
      /*if (oldGroup.group_keys) {
        for (const groupKey of oldGroup.group_keys) {
          console.log("onWelcome: my group_key:", bufferToBase64(groupKey));
        }
      }*/

      // the old group has the data_secrets, resumption_secrets, and group_keys that
      // we should prefer to maintain, so merge the new groups secrets into the old
      // then then move those arrays to the new group
      this.mergeLongTermSecrets(oldGroup, rtree.data_secrets,
        rtree.resumption_secrets, rtree.group_keys);

      rtree.resumption_secrets = oldGroup.resumption_secrets;
      rtree.data_secrets = oldGroup.data_secrets;
      rtree.group_keys = oldGroup.group_keys;
      rtree.lastMsgTime = oldGroup.lastMsgTime;
    } else {
      rtree.lastMsgTime = DateTime.now();
    }

    // printGroup(group);
    rtree.welcomed = true;
    rtree.outOfSync = false;
    rtree.unreadMentions = false;
    rtree.unreadMessages = false;
    if (frame.cmd === MessageFrameType.REWELCOME && oldGroup) {
      await delegate.updateRatchetTree(rtree);
    } else {
      await delegate.createRatchetTree(rtree);
    }
    await delegate.didProcessWelcome(frame, rtree);
  }

  async onExtWelcome(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    //console.log("Processing EXT_WELCOME message for group:", frame.groupID,
      //"from device:", frame.fromDeviceID, "user:", frame.fromUserID);
    if (!delegate.selfKeys.privateKey) {
      throw new Error("Missing Key Pair to decrypt External Welcome message");
    }
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }

    // TODO: verify signature

    let welMsg: ExternalWelcomeMessage;
    try {
      welMsg = this.decryptAndUnpack(frame.data, delegate.selfKeys, rtree) as ExternalWelcomeMessage;
    } catch (e) {
      console.error("Failed to decrypt EXT WELCOME data with selfKeys.privateKey", e);
      throw new Error("Failed to decrypt EXT WELCOME data");
    }
    const { resumption_secrets, data_secrets, private_keys, group_keys } = welMsg;

    this.mergeLongTermSecrets(rtree, data_secrets, resumption_secrets, group_keys);

    if (private_keys) {
      for (const entry of private_keys) {
        const node = rtree.treenodes[entry.i];
        if (node.privateKey) {
          if (node.privateKey !== entry.priv) {
            console.error("mismatching private keys on node:", entry.i, "group:", rtree._id);
            reportErrorInfo({
              message: "extadd_priv_key_mismatch",
              error: `node: ${entry.i} group: ${rtree._id}`,
              level: "error"
            });
          }
        } else {
          node.privateKey = entry.priv;
        }
      }
    }

    rtree.welcomed = true;
    await delegate.updateRatchetTree(rtree);
    await delegate.didProcessWelcome(frame, rtree);
  }

  private mergeLongTermSecrets(rtree: RatchetTree,
    data_secrets: Array<{ _id: string, secret: Uint8Array }>,
    resumption_secrets: Array<{ epoch: number, secret: Uint8Array }>,
    group_keys: Array<Uint8Array> | undefined) {
    const dataSet = new Set<string>();

    for (const dataSecret of rtree.data_secrets) {
      dataSet.add(dataSecret._id);
    }

    for (const dataSecret of data_secrets) {
      if (dataSet.has(dataSecret._id)) { continue; }
      rtree.data_secrets.push(dataSecret);
    }

    const rSet = new Set<number>();
    for (const rSecret of rtree.resumption_secrets) {
      rSet.add(rSecret.epoch);
    }

    for (const rSecret of resumption_secrets) {
      if (rSet.has(rSecret.epoch)) { continue; }
      rtree.resumption_secrets.push(rSecret);
    }
    rtree.resumption_secrets.sort((a, b) => a.epoch - b.epoch);

    if (rtree.group_keys && group_keys) {
      let i = 0;
      let mismatch = false;
      for (; i < rtree.group_keys.length; i++) {
        if (i >= group_keys.length) { break; }
        if (rtree.group_keys[i].length !== group_keys[i].length) {
          console.error("mismatching group key sizes at index:", i);
          mismatch = true;
          break;
        }
        let diff = false;
        for (let j = 0; j < rtree.group_keys[i].length; j++) {
          if (rtree.group_keys[i][j] !== group_keys[i][j]) {
            diff = true;
            break;
          }
        }
        if (diff) {
          console.error("mismatching group keys at index:", i);
          mismatch = true;
          break;
        }
      }
      if (mismatch) {
        console.error("Mismatching group keys on group:", rtree._id, "at index:", i);
        reportErrorInfo({
          message: "extadd_group_key_mismatch",
          error: `group: ${rtree._id} at index: ${i}`,
          level: "error"
        });
      } else {
        for (; i < group_keys.length; i++) {
          rtree.group_keys.push(group_keys[i]);
        }
      }
    } else if ((!rtree.group_keys || rtree.group_keys.length <= 0) &&
      group_keys && group_keys.length > 0) {
      rtree.group_keys = group_keys;
    }
  }

  async onExternalAdd(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      // console.log("ignoring my own ExternalAdd message, we don't have the keys to process it");
      return;
    }
    //console.log("processing External Add Message from:", frame.fromUserID, "counter:",
      //frame.msgCounter);

    const [rtree, device] = await bluebird.all([
      delegate.getRatchetTree(frame.groupID),
      delegate.getDevice(frame.fromDeviceID)
    ]);
    if (!rtree) {
      console.warn("no group found for add members message.. maybe we're adding this device.");
      return;
    }
    if (!device) {
      console.warn("no device found for joining ");
      return;
    }
    if (rtree.pki === GroupPKI.crystals && !device.verifyKey ||
      rtree.pki !== GroupPKI.crystals && !device.publicKey) {
      console.error("No public key available for device:", device);
      return;
    }
    if (device.userID !== frame.fromUserID) {
      console.error("External Add: Device does not belong to msg sender:", device, frame);
      return;
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }
    if (!rtree.externalPrivKey) {
      console.error("Missing external private key to process External Add msg");
      return;
    }

    this.verifyFrameSignature(frame, device, rtree); // will throw if fails

    let senderData: SenderData;
    try {
      senderData = this.decryptAndUnpack(frame.senderData, rtree.externalPrivKey, rtree) as SenderData;
    } catch (e) {
      console.error("Failed decrypting EXT ADD sender data with group external priv key:", e);
      throw new Error("Failed decrypting EXT ADD sender data");
    }
    let update: AddDevicesMessage;
    try {
      update = this.decryptAndUnpack(frame.data, rtree.externalPrivKey, rtree) as AddDevicesMessage;
    } catch (e) {
      console.error("Failed decrypting EXT ADD msg data with group external priv key:", e);
      throw new Error("Failed decrypting EXT ADD msg data");
    }

    if (update.devices.length > 1) {
      throw new Error("External Add can only specify at most one device to add");
    }
    if (!update.init_secret) {
      console.warn("Missing init_secret on external join message:", update, frame);
      return;
    }
    if (!update.derivationHash) {
      console.warn("Missing derivation hash in join message:", update, frame);
      return;
    }
    let isFirstJoin = true;
    for (const n of rtree.treenodes) {
      if (n.userID && n.userID === device.userID) {
        isFirstJoin = false;
        break;
      }
    }
    await this.processExternalJoin(rtree, update.devices[0], update.parents[0],
      update.init_secret, update.updatePath, senderData.sender, update.treeHash, update.derivationHash,
      delegate.selfDeviceID);

    this.treeService.updateMsgCounter(rtree, frame.msgCounter);

    await delegate.updateRatchetTree(rtree);
    // There's always at least one resumption secret. If we have more data than that, send EXT_WELCOME
    // otherwise, don't bother and hope another device can
    if (!frame.hasAcked &&
      (rtree.resumption_secrets.length >= 1 || rtree.data_secrets.length > 0)
      && (rtree.protocolScheme !== ProtocolScheme.mlsSharedSecret ||
        rtree.group_keys && rtree.group_keys.length > 0)) {
      if (!rtree.accountID) {
        throw new Error("Missing accountID on rtree");
      }
      const shouldProceed = await delegate.prackMessage(frame.accountID, frame.groupID, frame._id);
      if (shouldProceed) {
        const reply = this.externalWelcomeFor(rtree, device, delegate.selfDeviceID);
        await delegate.sendFrameTo(rtree.accountID, rtree._id, device._id, reply);
      }
    }

    const joinMsg: DeviceJoinedMessage = {
      _id: new ObjectId().toString(),
      msgtype: isFirstJoin ? MessageType.USER_JOINED : MessageType.DEV_JOINED,
      accountID: rtree.accountID,
      userID: device.userID,
      groupID: rtree._id,
      threadID: "",
      deviceID: device._id,
      deviceName: (device as any).displayString || device._id,
      created: frame.sent || frame.created
    };

    await delegate.onApplicationMessage(joinMsg, frame, rtree);
  }

  async onRecoverGroup(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      //console.log("ignoring my own RecoverGroup message, we don't have the keys to process it");
      return;
    }
    const [rtree, sendingDevice] = await bluebird.all([
      delegate.getRatchetTree(frame.groupID),
      delegate.getDevice(frame.fromDeviceID)
    ]);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }
    if (!sendingDevice) {
      throw new Error("Device of Sender not found");
    }
    if (sendingDevice.userID !== frame.fromUserID) {
      console.error("Recover: Device does not belong to msg sender:", sendingDevice, frame);
      throw new Error("Mismatching devices");
    }

    this.verifyFrameSignature(frame, sendingDevice, rtree); // will throw if fails

    const senderData = this.decryptAndUnpack(
      frame.senderData, rtree.externalPrivKey as string, rtree) as SenderData;
    const recoverMsg = this.decryptAndUnpack(
      frame.data, rtree.externalPrivKey as string, rtree) as RecoverMessage;

    if (!recoverMsg.init_secret) {
      console.warn("Missing init_secret on external join message:", recoverMsg, frame);
      return;
    }
    /*if (!update.derivationHash) {
      console.warn("Missing derivation hash in join message:", update, frame);
      return;
    }*/

    await this.processRecovery(rtree, sendingDevice, recoverMsg.resumeFromEpoch,
      recoverMsg.init_secret, recoverMsg.updatePath, senderData.sender,
      recoverMsg.treeHash, recoverMsg.derivationHash,
      delegate.selfDeviceID);

    this.treeService.updateMsgCounter(rtree, frame.msgCounter);
    rtree.outOfSync = false;

    await delegate.updateRatchetTree(rtree);

    if (!frame.hasAcked) {
      if (!rtree.accountID) {
        throw new Error("Missing accountID on rtree");
      }
      const reply = this.externalWelcomeFor(rtree, sendingDevice, delegate.selfDeviceID);
      await delegate.sendFrameTo(rtree.accountID, rtree._id, sendingDevice._id, reply);
    }
  }

  async onAddMembers(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      console.log("ignoring my own AddMembers message, we don't have the keys to process it");
      return;
    }
    //console.log("processing ADD_MEMBERS Message from:", frame.fromUserID, "counter:",
      //frame.msgCounter);
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      console.warn("no group found for add members message.. maybe we're adding this device.");
      return;
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }

    const { buf, senderNode, senderIndex } = await this.processIncomingMessage(
      rtree, frame, "handshake_secret", "commit");
    if (!senderNode.deviceID) {
      throw new Error("Device ID is Missing from Sender Node");
    }
    const update = this.unpackMessagePayload(buf) as AddDevicesMessage;
    if (update.devices.length !== update.parents.length) {
      throw new Error("Device count does not match parent count");
    }

    await this.processAddMembers(rtree, update.devices, update.parents,
      update.updatePath, senderIndex, update.treeHash, delegate.selfDeviceID);

    if (!rtree.treenodes[rtree.rootIndex].privateKey) {
      console.warn("Missing private key for ratchet tree root, index:", rtree.rootIndex);
    }

    const addMsg: DevicesAddedMessage = {
      _id: new ObjectId().toString(),
      accountID: rtree.accountID,
      userID: frame.fromUserID,
      groupID: rtree._id,
      threadID: "",
      msgtype: MessageType.DEV_ADDED,
      devices: update.devices.map(d => { return { _id: d._id, userID: d.userID }; }),
      created: moment()
    };
    await delegate.updateRatchetTree(rtree);

    await delegate.onApplicationMessage(addMsg, frame, rtree);
  }

  async onRemoveMember(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    //console.log("processing REMOVE_MEMBER message, counter:", frame.msgCounter);
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      console.log("ignoring my own REMOVE_MEMBER message, already processed it");
      return;
    }
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }

    const { buf, senderNode, senderIndex } = await this.processIncomingMessage(
      rtree, frame, "handshake_secret", "commit");

    const removeMsg = this.unpackMessagePayload(buf) as RemoveMemberMessage;
    const removeIndex = removeMsg.removed;

    if (removeIndex % 2 !== 0) {
      console.error("can only remove leaf nodes from tree, got:", removeIndex);
      return;
    }
    const nodeToRemove = rtree.treenodes[removeIndex];
    if (!nodeToRemove) {
      console.error("Remove Member: index:", removeIndex, "does not exist in group",
        rtree._id);
      return;
    }
    if (!nodeToRemove.userID) {
      console.error("userID missing from node to remove:",
        nodeToRemove, "group:", rtree._id);
      return;
    }
    if (!senderNode.userID) {
      console.error("tree node of sender is missing userID, index:", senderIndex,
        "group:", rtree._id);
      return;
    }
    if (!senderNode.deviceID) {
      throw new Error("Device ID is Missing from Sender Node");
    }
    if (senderNode.deviceID !== frame.fromDeviceID) {
      throw new Error("Mismatched device ID at sending node");
    }

    if (nodeToRemove.deviceID === delegate.selfDeviceID) {
      await delegate.removeRatchetTree(rtree);
      return;
    }

    await this.processRemoveMember(rtree, removeMsg.updatePath, removeIndex, removeMsg.treeHash,
      delegate.selfDeviceID);

    await delegate.updateRatchetTree(rtree);
    if (!rtree.treenodes[rtree.rootIndex].privateKey) {
      console.warn("Missing private key for ratchet tree root, index:", rtree.rootIndex);
    }
  }

  async onDeleteGroup(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    const data = frame.data;
    if (data.length > 0) {
      try {
        const instruct = this.unpackMessagePayload(data) as { clearContent: boolean };
        console.log("DELETE_GROUP message instructions:", instruct);
        if (instruct.clearContent) {
          await delegate.removeContent(frame.groupID, frame.accountID);
        }
      } catch (err) {
        console.error("Failed to unpack DELETE_GROUP data", err);
      }
    } else {
      console.log("no data in DELETE_GROUP message");
    }
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      return;
      //throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    await delegate.removeRatchetTree(rtree);
  }

  async onAppMessage(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      //console.log("ignoring my own MESSAGE message, already processed it");
      return;
    }
    //console.log("processing MESSAGE message, counter:", frame.msgCounter);

    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }

    const { buf } = rtree.protocolScheme === ProtocolScheme.mlsSharedSecret ?
      await this.processIncomingWithSharedSecret(rtree, frame, "application", true, delegate) :
      await this.processIncomingMessage(rtree, frame, "ratchet_secret", "application");

    await delegate.updateRatchetTree(rtree);

    //printGroup(rtree);
    const message = this.unpackMessagePayload(buf) as ContentMessage;
    await delegate.onApplicationMessage(message, frame, rtree);
  }

  async onRemoveMessage(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      console.log("ignoring my own REMOVE_MSG message, already processed it");
      return;
    }
    //console.log("processing REMOVE_MSG message, counter:", frame.msgCounter);
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }

    const { buf } = rtree.protocolScheme === ProtocolScheme.mlsSharedSecret ?
      await this.processIncomingWithSharedSecret(rtree, frame, "application", true, delegate) :
      await this.processIncomingMessage(rtree, frame, "ratchet_secret", "application");

    await delegate.updateRatchetTree(rtree);
    const message = this.unpackMessagePayload(buf) as RemoveMsgMessage;
    message.msgtype = MessageType.REMOVE_MSG;
    await delegate.onApplicationMessage(message, frame, rtree);
  }

  async onRemoveReply(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      console.log("ignoring my own REMOVE_REPLY message, already processed it");
      return;
    }
    //console.log("processing REMOVE_REPLY message, counter:", frame.msgCounter);
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }

    const { buf } = rtree.protocolScheme === ProtocolScheme.mlsSharedSecret ?
      await this.processIncomingWithSharedSecret(rtree, frame, "application", true, delegate) :
      await this.processIncomingMessage(rtree, frame, "ratchet_secret", "application");

    await delegate.updateRatchetTree(rtree);
    const message = this.unpackMessagePayload(buf) as RemoveMsgMessage;
    message.msgtype = MessageType.REMOVE_REPLY;
    await delegate.onApplicationMessage(message, frame, rtree);
  }

  async onDataKeyMessage(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      console.log("ignoring my own DATA_KEY message, already processed it");
      return;
    }
    //console.log("processing DATA_KEY message, counter:", frame.msgCounter);
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }
    const { buf } = rtree.protocolScheme === ProtocolScheme.mlsSharedSecret ?
      await this.processIncomingWithSharedSecret(rtree, frame, "commit", true, delegate) :
      await this.processIncomingMessage(rtree, frame, "handshake_secret", "commit");

    await delegate.updateRatchetTree(rtree);

    const message = this.unpackMessagePayload(buf) as NewDataKeyMessage;
    message.msgtype = MessageType.NEW_DATA_KEY;
    await delegate.onApplicationMessage(message, frame, rtree);
  }

  async onSchemeChange(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    if (frame.fromDeviceID === delegate.selfDeviceID) {
      console.log("ignoring my own SCHEME_CHANGE message, already processed it");
      return;
    }
    //console.log("processing SCHEME_CHANGE message, counter:", frame.msgCounter);
    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      console.log("no group found for scheme change message");
      return;
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }

    const { buf, senderIndex } = await this.processIncomingMessage(
      rtree, frame, "handshake_secret", "commit");

    const schemeChange = this.unpackMessagePayload(buf) as SchemeChangeMessage;

    await this.processSchemeChange(rtree, schemeChange.protocolScheme,
      schemeChange.group_key, schemeChange.updatePath, senderIndex, schemeChange.treeHash,
      delegate.selfDeviceID);

    const historyMsg: HistoryPreservationMessage = {
      _id: new ObjectId().toString(),
      accountID: rtree.accountID,
      userID: frame.fromUserID || "",
      groupID: rtree._id,
      threadID: "",
      msgtype: MessageType.HISTORY,
      isPreserved: schemeChange.protocolScheme === ProtocolScheme.mlsSharedSecret,
      created: DateTime.now()
    };
    await delegate.updateRatchetTree(rtree);
    await delegate.onApplicationMessage(historyMsg, frame, rtree);
  }

  async onUnknownMessage(frame: MessageFrame, delegate: ProtocolServiceDelegate) {
    // This hopefully keeps the ratchet tree in sync
    console.log("processing *unknown* message, counter:", frame.msgCounter);

    const rtree = await delegate.getRatchetTree(frame.groupID);
    if (!rtree) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    if (rtree.msgCounter > frame.msgCounter) {
      console.warn("we already processed this msg on group:", rtree._id,
        "group msgCounter:", rtree.msgCounter, "msg:", frame.msgCounter);
      return;
    }
    if (rtree.protoVersion < frame.version) {
      console.warn("Cannot process newer protocol version:", frame.version,
        "with group at version", rtree.protoVersion);
      return;
    }
    await this.processIncomingMessage(rtree, frame,
      "ratchet_secret", "application");
    await delegate.updateRatchetTree(rtree);
  }
}

let instance: ProtocolService;

export function protocolServiceFactory(cryptoService?: CryptoService): ProtocolService {
  if (!instance) { instance = new ProtocolServiceImpl(cryptoService); }
  return instance;
}

export function protocolServiceFactoryTest(cryptoService: CryptoService): ProtocolService {
  return new ProtocolServiceImpl(cryptoService)
}

export function bsonifyGroup(group: Group): BsonGroup {

  const bsonGroup: BsonGroup = {
    ...group,
    pki: group.pki || GroupPKI.p256,
    created: moment.isMoment(group.created) ? (group.created.toDate ?
      group.created.toDate() : new Date()) : group.created,
    treenodes: []
  };
  if (Object.hasOwnProperty.call(group, "lastMsgTime")) {
    delete (bsonGroup as any).lastMsgTime;
  }

  for (const key of (["sender_data_secret", "exporter_secret", "authentication_secret",
    "external_secret", "next_init_secret"] as Array<keyof GroupSecrets>)) {
    bsonGroup[key] = group[key];
  }

  bsonGroup.resumption_secrets = group.resumption_secrets?.map(s => {
    return { epoch: s.epoch, secret: s.secret };
  });

  if (group.group_keys) {
    bsonGroup.group_keys = Array.from(group.group_keys);
  }
  bsonGroup.treenodes = group.treenodes?.map((n): BsonRatchetTreeNode => {
    const node: BsonRatchetTreeNode = {
      userID: n.userID,
      deviceID: n.deviceID,
      generation: n.generation,
      handshakeGeneration: n.handshakeGeneration,
      privateKey: n.privateKey,
      publicKey: n.publicKey,
      signKey: n.signKey,
      verifyKey: n.verifyKey,
      nodeHash: n.nodeHash,
      ratchet_secret: n.ratchet_secret && !(n.ratchet_secret instanceof Uint8Array) ?
        new Uint8Array(n.ratchet_secret) : n.ratchet_secret,
      handshake_secret: n.handshake_secret && !(n.handshake_secret instanceof Uint8Array) ?
        new Uint8Array(n.handshake_secret) : n.handshake_secret
    };
    return node;
  });

  return bsonGroup;
}

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
function printGroup(group: Group) {
  const startIndex = group.pki === GroupPKI.crystals ? 1200 : 90;
  console.log("  epoch:", group.epoch);
  console.log("  counter:", group.msgCounter);
  console.log("  sender:", group.sender_data_secret ? bufferToBase64(group.sender_data_secret) : "none");
  console.log("  next_init_secret:", group.next_init_secret ? bufferToBase64(group.next_init_secret) : "none");
  console.log(" nodes:", group.treenodes?.length || -1);

  if (!group.treenodes) { return; }
  for (let i = 0; i < group.treenodes.length; i++) {
    const node = group.treenodes[i];
    console.log(" ", i, ":", node.generation, node.handshakeGeneration,
      "r:", node.ratchet_secret ? bufferToBase64(node.ratchet_secret) : "none",
      "h:", node.handshake_secret ? bufferToBase64(node.handshake_secret) : "none",
      "#:", node.nodeHash ? bufferToBase64(node.nodeHash) : "none");
    console.log("          pub:", node.publicKey?.substring(startIndex), "priv:", node.privateKey?.substring(startIndex) || "nil");
    console.log("          ver:", node.verifyKey?.substring(startIndex));
  }
}
/* eslint-enable @typescript-eslint/ban-ts-comment */
/* eslint-enable @typescript-eslint/no-unused-vars */
